(ns ui.components.detail-card.view
  "The view-only card of ZapKit: a card with the label and the value of saved fields, for a drawer,
   a modal or a detail panel. Use it to show data that the user does not edit. Do not use a
   disabled Input for that.

   ZapKit has no `styles.json` for it. The classes are the classes of `detailCardShellClassName`
   (`DetailCard/detail-card-shell.styles.ts`) and of the view only recipe (`VIEW_ONLY_FIELDS.md`,
   `shared/typography.styles.ts`) of zapkit-components, with the `tw2-` prefix. An empty value
   shows a dash and the row stays, as the recipe says."
  (:require
   [clojure.string :as string]
   [ui.util :as uu]))

(def dom-overrides
  "Classes that the generated styles do not have. `:card` is the shell of the card. `:title` is the
   title of a section (`viewSectionTitle`). `:field` stacks a label on its value. `:label`,
   `:value` and `:value-empty` are `viewFieldLabel`, `viewFieldValue` and `viewFieldValueEmpty`.
   `:value` also breaks a long word, so that a long text stays inside the card."
  {:card        "tw2-flex tw2-w-full tw2-flex-col tw2-gap-field-gap tw2-rounded-card tw2-border tw2-border-card-border-default tw2-bg-card-bg-default tw2-p-card-padding tw2-shadow-2xs"
   :title       "tw2-text-eyebrow tw2-uppercase tw2-tracking-wider tw2-text-tertiary"
   :field       "tw2-flex tw2-flex-col tw2-gap-field-gap"
   :label       "tw2-text-label-sm tw2-text-tertiary"
   :value       "tw2-text-body-md tw2-text-primary tw2-font-semibold tw2-break-words"
   :value-empty "tw2-text-body-md tw2-text-tertiary"})

(def empty-value-text
  "The text of an empty value. It is a dash, not a word, so it needs no translation."
  "—")

(defn blank-value?
  "True when a value has nothing to show: nil, or a blank string."
  [value]
  (or (nil? value)
      (and (string? value)
           (string/blank? value))))

(defn section-title
  "The title of a section (`viewSectionTitle`). The card shows it over its fields; on its own it
   titles other read-only content in the card's style, such as a column. `:class` is added, for
   its place in a layout."
  [{:keys [class]} text]
  [:h3 {:class (uu/cm (:title dom-overrides) "m-0" class)}
   text])

(defn field-label
  "The label of a field (`viewFieldLabel`). `field` shows it over a plain value; on its own it
   labels a value that is not plain text, such as a choice between two values. `:class` is added,
   for its place in a layout."
  [{:keys [class]} text]
  [:span {:class (uu/cm (:label dom-overrides) class)}
   text])

(defn field
  "One field of a card: its `:label` above its `:value`. A blank value shows `empty-value-text` in
   the style of an empty value."
  [{:keys [label value]}]
  (let [blank? (blank-value? value)]
    [:div {:class (:field dom-overrides)}
     (field-label {} label)
     [:span {:class (if blank?
                      (:value-empty dom-overrides)
                      (:value dom-overrides))}
      (if blank?
        empty-value-text
        value)]]))

(defn view
  "The card. `:title` is the optional title of the section, `:class` is added to the card. The
   children are the fields, see `field`:

     [detail-card/view {:title \"Opportunity\"}
      [detail-card/field {:label \"Owner\" :value \"Ana Garcia\"}]]"
  [{:keys [title class]} & children]
  (into [:section {:class (uu/cm (:card dom-overrides) class)}
         (when title
           (section-title {} title))]
        children))
